import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { logOut, setErrorMessage } from "../redux/actions";
import type { ReportExport } from "@commercial-publishing/shared-types/report";

const baseQuery = fetchBaseQuery({
  baseUrl: `${import.meta.env.VITE_BACKEND_BASE_URL}/v1`,
  credentials: "include",
  prepareHeaders: (headers) => {
    headers.set("accept", "application/json");
    return headers;
  },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    api.dispatch(logOut());
  } else if (result.error) {
    const errorMessage =
      result.error.data || result.error.message || "An error occurred";
    api.dispatch(setErrorMessage(`Error: ${errorMessage}`));
  }

  return result;
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Project", "UserFavorites", "Report", "ReportExport"],
  endpoints: (builder) => ({
    // ===== PROJECT ENDPOINTS =====
    getProjects: builder.query({
      query: ({ type, page = 1, search } = {}) => {
        const params = new URLSearchParams();
        if (type) params.append("type", type);
        if (page > 1) params.append("page", page.toString());
        if (search) params.append("search", search);
        return `projects?${params.toString()}`;
      },
      providesTags: (result, _error, { type }) => {
        if (result) {
          return [
            ...result.map(({ _id }) => ({ type: "Project", id: _id })),
            { type: "Project", id: "LIST" },
            ...(type === "favorites"
              ? [{ type: "UserFavorites", id: "LIST" }]
              : []),
          ];
        }
        return [{ type: "Project", id: "LIST" }];
      },
    }),

    getProject: builder.query({
      query: (id) => `projects/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Project", id }],
    }),

    createProject: builder.mutation({
      query: (newProject) => ({
        url: "projects",
        method: "POST",
        body: newProject,
      }),
      invalidatesTags: [{ type: "Project", id: "LIST" }],
    }),

    updateProject: builder.mutation({
      query: ({ patch }) => ({
        url: `projects/${patch._id}`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: (_result, _error, { patch }) => {
        return [
          { type: "Project", id: patch._id },
          { type: "Project", id: "LIST" },
          // This will now invalidate reports in the same cache!
        ];
      },
    }),

    deleteProject: builder.mutation({
      query: (id) => ({
        url: `projects/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "Project", id },
        { type: "Project", id: "LIST" },
      ],
    }),

    toggleFavorite: builder.mutation({
      query: (id) => ({
        url: `projects/fav/${id}`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "UserFavorites", id: "LIST" },
        { type: "Project", id: "LIST" },
      ],
    }),

    getMe: builder.query({
      query: () => "me",
    }),

    // ===== REPORT ENDPOINTS =====
    getReportById: builder.query({
      query: ({ id, dateFrom, dateTo }) => ({
        url: `reports/${id}`,
        ...(dateFrom &&
          dateTo && {
            params: {
              dateFrom: dateFrom,
              dateTo: dateTo,
            },
          }),
      }),
      transformErrorResponse: (response) => {
        let errorMessage = "Error loading report";
        if (response.status && response.data) {
          errorMessage += `: ${response.data}`;
        }
        return {
          originalError: response,
          message: errorMessage,
          status: response.status,
          isAuthError: response.status === 401,
        };
      },
      providesTags: (result, _error, { id }) => {
        return [
          { type: "Report", id },
          { type: "Report", id: "LIST" },
          // Tag with the project ID - now in the same cache as projects!
          ...(result?.project?._id
            ? [{ type: "Project", id: result.project._id }]
            : []),
        ];
      },
    }),

    // ===== REPORT EXPORT ENDPOINTS =====
    getReportExports: builder.query<ReportExport[], string>({
      query: (id) => `reports/${id}/exports`,
      providesTags: (_result, _error, id) => [{ type: "ReportExport", id }],
    }),

    createReportExport: builder.mutation<
      ReportExport,
      { id: string; dateFrom?: string; dateTo?: string }
    >({
      query: ({ id, dateFrom, dateTo }) => ({
        url: `reports/${id}/exports`,
        method: "POST",
        body: {
          ...(dateFrom && { dateFrom }),
          ...(dateTo && { dateTo }),
        },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ReportExport", id },
      ],
    }),

    deleteReportExport: builder.mutation<
      void,
      { id: string; exportId: string }
    >({
      query: ({ id, exportId }) => ({
        url: `reports/${id}/exports/${exportId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ReportExport", id },
      ],
    }),
  }),
});

export const {
  // Project hooks
  useGetProjectsQuery,
  useGetProjectQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useToggleFavoriteMutation,
  useGetMeQuery,
  // Report hooks
  useGetReportByIdQuery,
  // Report export hooks
  useGetReportExportsQuery,
  useCreateReportExportMutation,
  useDeleteReportExportMutation,
} = api;
